import { useSelector } from '@tanstack/react-form';
import { useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  fieldsForSides,
  sourceForType,
  useCardTemplates,
} from '../../../api/cards';
import { useIseeEnabled, useIseeSessions } from '../../../api/isee';
import { useLuccaEnabled } from '../../../api/lucca';
import { withForm } from '../../../form-hook';
import { downloadBlob } from '../../../utils/downloadBlob';
import { formProps } from '../../../utils/form-props';
import { ErrorMessage } from '../../common/ErrorMessage';
import type { CardsSearch, CardsSearchPatch } from '../cards-url-state';
import { EmployeeLoginPicker } from '../EmployeeLoginPicker';
import { FileDropZone } from '../FileDropZone';
import { IseeRecipients } from '../IseeRecipients';
import type { CsvDelimiter, CsvPreview, TemplateMeta } from './card-batch';
import {
  csvTemplateColsFor,
  effectiveSidesFor,
  selectedTplFor,
} from './card-batch';
import { CsvPreviewSection } from './CsvPreviewSection';
import { cardGenFormOpts } from './use-card-batch';

/**
 * Step 2 — who the cards are for. Which of the three appears is decided by the
 * selected template's type, not by the operator: a student template resolves
 * through ISEE, an employee one through Lucca, anything else from a CSV.
 */
export const RecipientsStep = withForm({
  ...cardGenFormOpts,
  props: formProps<{
    /** What the chosen template's SVGs carry (vars + defaults). */
    template: TemplateMeta;
    /** Parsed CSV preview, null until one is loaded. */
    csv: CsvPreview | null;
    search?: CardsSearch;
    onCsvFile: (file: File) => void;
    onSearchPatch?: (patch: CardsSearchPatch) => void;
  }>(),
  render: function RecipientsStep({
    form,
    template,
    csv,
    search,
    onCsvFile,
    onSearchPatch,
  }) {
    const { t } = useTranslation();
    const iseeEnabled = useIseeEnabled();
    const luccaEnabled = useLuccaEnabled();
    // The picker stays hidden while probing / if ISEE is unreachable.
    const iseeSessions = useIseeSessions(iseeEnabled);
    const savedTemplates = useCardTemplates();
    const csvFile = useSelector(form.store, (state) => state.values.csvFile);
    const templateId = useSelector(
      form.store,
      (state) => state.values.templateId,
    );
    const templateFile = useSelector(
      form.store,
      (state) => state.values.templateFile,
    );
    const csvDelimiter = useSelector(
      form.store,
      (state) => state.values.delimiter,
    );
    const csvDelimiterIsAuto = useSelector(
      form.store,
      (state) => state.values.delimiterIsAuto,
    );
    const sides = useSelector(form.store, (state) => state.values.sides);
    const iseeQuery = useSelector(
      form.store,
      (state) => state.values.iseeQuery,
    );
    // Derived here from what this step already subscribes to, so the parent
    // does not have to subscribe to the whole form to hand them down.
    const selectedTpl = selectedTplFor(templateId, savedTemplates);
    const source = sourceForType(selectedTpl?.type);
    const csvTemplateCols = useMemo(
      () =>
        csvTemplateColsFor(
          fieldsForSides(
            effectiveSidesFor(selectedTpl, sides),
            template.frontVars,
            template.backVars,
          ),
          template.defaults,
        ),
      [selectedTpl, sides, template],
    );

    /** A blank CSV with just the columns this template expects. */
    const downloadCsvTemplate = () => {
      const name =
        savedTemplates.find((tpl) => tpl.id === templateId)?.name ??
        templateFile?.name.replace(/\.svg$/i, '') ??
        'modele';
      downloadBlob(
        new Blob([csvTemplateCols.join(',') + '\n'], { type: 'text/csv' }),
        `${name}.csv`,
      );
    };

    return (
      <>
        {/* Recipients — the source follows the selected template's type:
        CSV (other/custom), ISEE filters (student), Lucca logins (employee). */}

        {/* CSV — avec bouton de téléchargement du modèle si template chargé */}
        {source === 'csv' && (
          <CsvRecipients
            templateFile={templateFile}
            csvFile={csvFile}
            csvTemplateCols={csvTemplateCols}
            csv={csv}
            delimiter={csvDelimiter}
            delimiterIsAuto={csvDelimiterIsAuto}
            onDownloadTemplate={downloadCsvTemplate}
            onCsvFile={onCsvFile}
            onDelimiterChange={(d) => {
              form.setFieldValue('delimiter', d);
              form.setFieldValue('delimiterIsAuto', false);
            }}
          />
        )}

        {source === 'isee' && (
          <IseeRecipients
            iseeEnabled={iseeEnabled}
            iseeSessions={iseeSessions}
            query={iseeQuery}
            onQueryChange={(q) => {
              form.setFieldValue('iseeQuery', q);
              onSearchPatch?.(q);
            }}
          />
        )}

        {source === 'lucca' &&
          (luccaEnabled ? (
            <EmployeeLoginPicker
              initialLogins={search?.logins}
              onChange={(logins) => {
                form.setFieldValue('logins', logins);
                onSearchPatch?.({
                  logins: logins.length > 0 ? logins : undefined,
                });
              }}
            />
          ) : (
            <ErrorMessage message={t('cardGenerator.employee.disabled')} />
          ))}
      </>
    );
  },
});

function CsvRecipients({
  templateFile,
  csvFile,
  csvTemplateCols,
  csv,
  delimiter,
  delimiterIsAuto,
  onDownloadTemplate,
  onCsvFile,
  onDelimiterChange,
}: {
  templateFile: File | null;
  csvFile: File | null;
  csvTemplateCols: string[];
  csv: CsvPreview | null;
  delimiter: CsvDelimiter;
  delimiterIsAuto: boolean;
  onDownloadTemplate: () => void;
  onCsvFile: (file: File) => void;
  onDelimiterChange: (d: CsvDelimiter) => void;
}) {
  const { t } = useTranslation();
  const captionId = useId();
  const hint = templateFile
    ? t('cardGenerator.csvHintWithCols', { cols: csvTemplateCols.join(', ') })
    : t('cardGenerator.csvHint');
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <p id={captionId} className="block text-xs font-medium text-ink">
          {t('cardGenerator.csvLabel')}
        </p>
        {templateFile && (
          <button
            type="button"
            onClick={onDownloadTemplate}
            className="flex items-center gap-1 text-xs text-blue transition-colors"
          >
            <svg
              className="size-3.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
            {t('cardGenerator.downloadCsvTemplate')}
          </button>
        )}
      </div>
      <FileDropZone
        labelledBy={captionId}
        accept=".csv,text/csv"
        hint={hint}
        file={csvFile}
        onFile={onCsvFile}
        summary={undefined}
      />
      {csv && (
        <CsvPreviewSection
          delimiter={delimiter}
          isAuto={delimiterIsAuto}
          onDelimiterChange={onDelimiterChange}
          columns={csv.columns}
          rawColumns={csv.rawColumns}
          rows={csv.rows}
          totalRows={csv.total}
        />
      )}
    </div>
  );
}
